import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export type CoreUISwitchSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'core-ui-switch',
  standalone: false,
  templateUrl: './switch.component.html',
  styleUrls: ['./switch.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CoreUISwitchComponent),
      multi: true,
    },
  ],
})
export class CoreUISwitchComponent implements ControlValueAccessor {
  @Input() size: CoreUISwitchSize = 'md';
  @Input() label = '';
  @Input() ariaLabel = '';
  @Input() name: string | null = null;
  @Input() value = 'on';
  @Input() required = false;
  @Input() readOnly = false;
  @Input() disabled = false;
  @Input() showIcon = true;

  @Output() checkedChange = new EventEmitter<boolean>();

  private currentValue = false;
  private formDisabled = false;
  private onChange: (value: boolean) => void = () => {};
  private onTouched: () => void = () => {};

  @Input()
  get checked(): boolean {
    return this.currentValue;
  }

  set checked(value: boolean) {
    this.currentValue = Boolean(value);
  }

  get isDisabled(): boolean {
    return this.disabled || this.formDisabled;
  }

  get iconSize(): number {
    if (this.size === 'sm') return 9;
    if (this.size === 'lg') return 14;
    return 12;
  }

  onToggle(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (this.readOnly) {
      input.checked = this.currentValue;
      return;
    }

    this.currentValue = input.checked;
    this.onChange(this.currentValue);
    this.onTouched();
    this.checkedChange.emit(this.currentValue);
  }

  markAsTouched(): void {
    this.onTouched();
  }

  writeValue(value: boolean | null): void {
    this.currentValue = Boolean(value);
  }

  registerOnChange(fn: (value: boolean) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.formDisabled = disabled;
  }
}
